import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { InterestsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as InterestActions from "../../../actions/interests";
import * as NavigationActions from "../../../actions/navigation";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { LookupsLayout } from "../../index";

import "./InterestsPage.scss";

interface InterestsPageProps {
    formFilter: IFormFilter;
    interest: Dtos.Interest[];
    loadingInterests: boolean;
    loadInterestsSuccess: boolean;
    loadInterestsFailure: boolean;
    loadInterestsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInterests: InterestActions.IInterestClearActionCreator;
    loadInterests: InterestActions.IInterestLoadActionCreator;
    filter: InterestActions.IInterestFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const InterestsPage: FC<InterestsPageProps> = ({
    formFilter,
    interest,
    loadingInterests,
    loadInterestsSuccess,
    loadInterestsFailure,
    loadInterestsErrorCode,
    permissions,
    clearInterests,
    loadInterests,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showInterests = !loadingInterests && loadInterestsSuccess;


    // Load interests
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(InterestsBreadcrumbs());

            loadInterests();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        InterestsBreadcrumbs,
        loadInterests,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInterestsFailure) {
            navigate(`/error/${loadInterestsErrorCode}`);
        }
    }, [loadInterestsFailure, loadInterestsErrorCode, navigate]);

    // Cleanup effect to clear the interests on component unmount
    useEffect(() => {
        return () => {
            clearInterests();
        };
    }, [clearInterests]);

    const createTable = useMemo(() => {
        if (!interest || interest.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no interests to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = interest.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={interest}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? DEFAULT_PAGE_SIZE : interest.length
                    }
                    pageSize={showPagination ? undefined : interest.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no interest to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/general/interests/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [interest, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Interests</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InterestsCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/general/interests/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInterests ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInterests ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showInterests, createTable]);

    return (
        <LookupsLayout
            loading={loadingInterests}
            permission={Dtos.Permission.InterestsView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<InterestsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.interests.formFilter,
        interest: state.interests.formFilteredData,
        loadingInterests:
            state.interests.loadState &&
            state.interests.loadState.status == RequestState.Pending,
        loadInterestsSuccess:
            state.interests.loadState &&
            state.interests.loadState.status == RequestState.Success,
        loadInterestsFailure:
            state.interests.loadState &&
            state.interests.loadState.status == RequestState.Failure,
        loadInterestsErrorCode: state.interests.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<InterestsPageProps> => {
    return {
        loadInterests: bindActionCreators(
            InterestActions.LoadInterest,
            dispatch
        ),
        clearInterests: bindActionCreators(InterestActions.Clear, dispatch),
        filter: bindActionCreators(InterestActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(InterestsPage);
